import {
  Box,
  Button,
  Container,
  Fieldset,
  Flex,
  Image,
  Input,
  Text,
  Link as ChakraLink
} from '@chakra-ui/react';
import logo from '@/assets/icon/logo.svg';
import loginBanner from '@/assets/image/login-banner.svg';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom';
import { fetchAPI } from '@/api/fetch';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { Checkbox } from '@/components/ui/checkbox';
import useAdminAuth from '@/store/adminAuth';
import { apiAdminLogin } from '@/api/services';

type FormValues = {
  loginEmail: string;
  loginPassword: string;
  rememberMe: boolean;
};

export default function AdminLogin() {
  const { login, isLoggedIn } = useAdminAuth();
  const navigate = useNavigate();

  const [isIncorrect, setIsIncorrect] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({ shouldFocusError: false });

  const rememberMe = watch('rememberMe');

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/admin');
    }

    const storedEmail = localStorage.getItem('admin_email');
    const storedPassword = localStorage.getItem('admin_password');

    if (storedEmail && storedPassword) {
      setValue('loginEmail', storedEmail);
      setValue('loginPassword', atob(storedPassword)); // Decode password
      setValue('rememberMe', true);
    } else {
      setValue('rememberMe', false);
    }
  }, [isLoggedIn, navigate, setValue]);

  const handleLogin = async (values: FormValues) => {
    setIsIncorrect(false);

    const response = await fetchAPI({
      request: () =>
        apiAdminLogin({
          email: values.loginEmail,
          password: values.loginPassword
        })
    });

    if (response.success) {
      if (values.rememberMe) {
        localStorage.setItem('admin_email', values.loginEmail);
        localStorage.setItem('admin_password', btoa(values.loginPassword)); // Encode password
      } else {
        localStorage.removeItem('admin_email');
        localStorage.removeItem('admin_password');
      }

      const { admin, access_token: accessToken, refresh_token: refreshToken } = response.data;
      login(admin, accessToken, refreshToken);
      navigate('/admin');
    } else {
      console.error('Login failed:', response.message);
      setIsIncorrect(true);
    }
  };

  return (
    <Container
      maxW="100%"
      minH="100vh"
      w="100%"
      h="100vh"
      p={0}
      bg="background.secondary"
      display="flex"
      flexDir="column"
      overflow="hidden"
    >
      <Flex flexGrow={1} w="100%" h="100%" justifyContent="center" alignItems="center">
        <Box w="50%">
          <header style={{ position: 'fixed', top: 0, width: '100%', zIndex: 1000 }}>
            <Flex p="32px 50px">
              <Image src={logo} alt="logo" width="145px" />
            </Flex>
          </header>
          <form onSubmit={handleSubmit(handleLogin)} style={{ marginTop: '100px' }}>
            <Fieldset.Root
              borderRadius="10px"
              w="100%"
              h="725px"
              p="48px 135px"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box>
                <Flex justifyContent="left">
                  <Text fontSize="36px" fontWeight={700}>
                    Admin Login
                  </Text>
                </Flex>
                <Box mt="48px" />
                <Box>
                  <Text fontWeight={500} fontSize="16px" color="neutrals.900" mb="10px">
                    Email
                  </Text>
                  <Input
                    fontSize="16px"
                    fontWeight={400}
                    id="login-email"
                    placeholder="Enter your email"
                    _placeholder={{ color: 'neutrals.300' }}
                    borderColor={errors.loginEmail || isIncorrect ? 'error.500' : 'neutrals.300'}
                    variant="outline"
                    p="12px 16px"
                    h="48px"
                    autoComplete="email"
                    borderRadius="8px"
                    _invalid={{ borderWidth: 1, borderColor: 'error.500' }}
                    {...register('loginEmail', {
                      required: 'Email is required.',
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: 'Please enter a valid email address.'
                      }
                    })}
                  />
                  {(errors.loginEmail || isIncorrect) && (
                    <Text fontSize="14px" color="error.500" mt="5px" ml="5px">
                      {errors.loginEmail
                        ? errors.loginEmail.message
                        : 'Email or password is incorrect. Please try again.'}
                    </Text>
                  )}
                </Box>
                <Box mt="20px">
                  <Text fontWeight={500} fontSize="16px" color="neutrals.900" mb="10px">
                    Password
                  </Text>
                  <Flex alignItems="center" position="relative">
                    <Input
                      fontSize="16px"
                      fontWeight={400}
                      id="login-password"
                      placeholder="Enter your password"
                      _placeholder={{ color: 'neutrals.300' }}
                      borderColor={
                        errors.loginPassword || isIncorrect ? 'error.500' : 'neutrals.300'
                      }
                      variant="outline"
                      type={showPassword ? 'text' : 'password'}
                      p="12px 16px"
                      h="48px"
                      autoComplete="current-password"
                      borderRadius="8px"
                      _invalid={{ borderWidth: 1, borderColor: 'error.500' }}
                      {...register('loginPassword', {
                        required: 'Password is required.'
                      })}
                    />
                    <Box
                      onClick={() => setShowPassword(!showPassword)}
                      position="absolute"
                      right="10px"
                      bg="transparent"
                      _hover={{ bg: 'transparent', cursor: 'pointer' }}
                      _active={{ bg: 'transparent' }}
                      _focus={{ boxShadow: 'none' }}
                    >
                      {showPassword ? (
                        <EyeSlash size={24} />
                      ) : (
                        <Eye color="neutrals.900" size={24} />
                      )}
                    </Box>
                  </Flex>
                  {(errors.loginPassword || isIncorrect) && (
                    <Text fontSize="14px" color="error.500" mt="5px" ml="5px">
                      {errors.loginPassword
                        ? errors.loginPassword.message
                        : 'Email or password is incorrect. Please try again.'}
                    </Text>
                  )}
                </Box>
                <Box mt="16px">
                  <Flex alignItems="center">
                    <Checkbox
                      id="remember-me"
                      checked={rememberMe}
                      colorPalette="gray"
                      variant="subtle"
                      cursor="pointer"
                      {...register('rememberMe')}
                    >
                      <Text fontSize="16px" color="neutrals.500">
                        Remember me
                      </Text>
                    </Checkbox>
                  </Flex>
                </Box>
                <Box mt="32px">
                  <Button
                    loading={isSubmitting}
                    bg="primary"
                    w="100%"
                    h="60px"
                    color="neutrals.0"
                    borderRadius="8px"
                    type="submit"
                    style={{
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    Log in
                  </Button>
                </Box>
                <Box mt="32px" textAlign="center">
                  <ChakraLink
                    asChild
                    fontSize="14px"
                    fontWeight={400}
                    lineHeight={1.167}
                    color="primary.900"
                    textDecoration="none"
                    cursor="pointer"
                  >
                    <ReactRouterLink to="/forget-password">Forgot your password?</ReactRouterLink>
                  </ChakraLink>
                </Box>
              </Box>
            </Fieldset.Root>
          </form>
        </Box>
        <Box w="50%" display="flex" justifyContent="center" alignItems="center">
          <Image src={loginBanner} alt="Login Illustration" maxW="100%" maxH="100%" />
        </Box>
      </Flex>
    </Container>
  );
}
